import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  getDMs,
  getDMThread,
  updateDMThread,
  addDMParticipant,
  removeDMParticipant,
  setDMModerator,
  getDMMessages,
  sendDMMessage,
  markDMRead,
  createOrFindDM,
  deleteDMMessage,
} from '../services/dmService';
import { lookupUsers } from '../services/userService';
import { uploadFile, downloadFile, openFile } from '../services/fileService';
import MessageCompose from '../components/MessageCompose';
import './MessagesPage.css';

function timeAgo(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export default function MessagesPage() {
  const { user } = useAuth();
  const location = useLocation();
  const bottomRef = useRef(null);
  const soundRef = useRef(null);
  const composeFileRef = useRef(null);
  const lastMsgCountRef = useRef(0);
  const activeThreadRef = useRef(null);

  const [threads, setThreads] = useState([]);
  const [activeThread, setActiveThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msgLoading, setMsgLoading] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeToQuery, setComposeToQuery] = useState('');
  const [composeMatches, setComposeMatches] = useState([]);
  const [composeRecipient, setComposeRecipient] = useState(null);
  const [composeBody, setComposeBody] = useState('');
  const [composeFiles, setComposeFiles] = useState([]);
  const [composeSending, setComposeSending] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [settingsThread, setSettingsThread] = useState(null);
  const [settingsTitle, setSettingsTitle] = useState('');
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsQuery, setSettingsQuery] = useState('');
  const [settingsMatches, setSettingsMatches] = useState([]);
  const [isMobileViewport, setIsMobileViewport] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= 1024;
  });
  const [mobilePane, setMobilePane] = useState('list');

  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth <= 1024;
      setIsMobileViewport(mobile);
      if (!mobile) setMobilePane('list');
    };

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Init notification sound
  useEffect(() => {
    soundRef.current = new Audio('/sound/Message.wav');
    soundRef.current.volume = 0.6;
    return () => { soundRef.current = null; };
  }, []);

  const fetchDMs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getDMs();
      setThreads(res.data.threads || []);
    } catch (err) {
      console.error('Failed to load DMs:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDMs(); }, [fetchDMs]);

  // If navigated here from Contacts with a pre-selected thread
  useEffect(() => {
    const activeId = location.state?.activeThreadId;
    if (!activeId || threads.length === 0) return;
    const found = threads.find((t) => t._id === activeId);
    if (found) openThread(found);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threads, location.state?.activeThreadId]);

  const openThread = async (thread) => {
    setActiveThread(thread);
    setShowSettings(false);
    setSettingsThread(null);
    activeThreadRef.current = thread;
    lastMsgCountRef.current = 0;
    setMessages([]);
    setMsgLoading(true);
    try {
      const res = await getDMMessages(thread._id);
      const msgs = res.data.messages || [];
      setMessages(msgs);
      if (isMobileViewport) setMobilePane('detail');
      lastMsgCountRef.current = msgs.length; // baseline — no sound on initial load
      setThreads((prev) =>
        prev.map((t) => (t._id === thread._id ? { ...t, unreadCount: 0 } : t))
      );
      markDMRead(thread._id).catch(() => {});
    } catch (err) {
      console.error('Failed to load DM messages:', err);
    } finally {
      setMsgLoading(false);
    }
  };

  // Scroll to bottom when messages load
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Poll active thread for new messages every 4 s
  useEffect(() => {
    if (!activeThread) return;
    const poll = async () => {
      try {
        const res = await getDMMessages(activeThread._id);
        const msgs = res.data.messages || [];
        if (msgs.length > lastMsgCountRef.current) {
          const newMsgs = msgs.slice(lastMsgCountRef.current);
          const hasIncoming = newMsgs.some(
            (m) => String(m.sender?._id) !== String(user?._id)
          );
          if (hasIncoming && soundRef.current) {
            soundRef.current.currentTime = 0;
            soundRef.current.play().catch(() => {});
          }
          setMessages(msgs);
          lastMsgCountRef.current = msgs.length;
          markDMRead(activeThread._id).catch(() => {});
          setThreads((prev) =>
            prev.map((t) =>
              t._id === activeThread._id
                ? { ...t, unreadCount: 0, lastMessageAt: new Date().toISOString() }
                : t
            )
          );
        }
      } catch (_) { /* silent */ }
    };
    const id = setInterval(poll, 4000);
    return () => clearInterval(id);
  }, [activeThread, user?._id]);

  // Poll thread list every 15 s for new DMs / unread badges
  useEffect(() => {
    const id = setInterval(fetchDMs, 15000);
    return () => clearInterval(id);
  }, [fetchDMs]);

  useEffect(() => {
    const q = composeToQuery.trim();
    if (!composeOpen || !q || composeRecipient) {
      setComposeMatches([]);
      return;
    }

    let ignore = false;
    lookupUsers(q)
      .then((res) => {
        if (ignore) return;
        setComposeMatches(res.data.users || []);
      })
      .catch(() => {
        if (!ignore) setComposeMatches([]);
      });

    return () => {
      ignore = true;
    };
  }, [composeOpen, composeToQuery, composeRecipient]);

  const handleSend = async ({ body, files }) => {
    const recipientId = getOther(activeThread)?._id;
    const uploadedAttachments = [];

    for (const file of files || []) {
      const fileRes = await uploadFile(null, file, null, recipientId ? [recipientId] : []);
      uploadedAttachments.push({
        fileRef: fileRes.data.file._id,
        name: fileRes.data.file.originalName || file.name,
      });
    }

    const res = await sendDMMessage(activeThread._id, { body, attachments: uploadedAttachments });
    setMessages((prev) => [...prev, res.data.message]);
    lastMsgCountRef.current += 1; // keep poll in sync — don't re-count our own send
    setThreads((prev) =>
      prev.map((t) =>
        t._id === activeThread._id ? { ...t, lastMessageAt: new Date().toISOString() } : t
      )
    );
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await deleteDMMessage(messageId);
      setMessages((prev) => prev.filter((m) => m._id !== messageId));
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to delete message');
    }
  };

  const getThreadDisplayName = useCallback((thread) => {
    if (!thread) return 'Direct Message';
    const subject = String(thread.subject || '').trim();
    if (subject && subject !== 'Direct Message') return subject;

    const participantNames = (thread.participants || [])
      .filter((p) => String(p?._id || p) !== String(user?._id))
      .map((p) => p?.name)
      .filter(Boolean);

    if (participantNames.length > 0) {
      return participantNames.join(', ');
    }

    const other = getOther(thread);
    return other.name || 'Direct Message';
  }, [user?._id]);

  const mergeThreadIntoState = useCallback((thread) => {
    setThreads((prev) => prev.map((entry) => (entry._id === thread._id ? { ...entry, ...thread } : entry)));
    if (activeThread?._id === thread._id) {
      setActiveThread((prev) => ({ ...prev, ...thread }));
      activeThreadRef.current = { ...activeThreadRef.current, ...thread };
    }
  }, [activeThread?._id]);

  const loadThreadSettings = useCallback(async () => {
    if (!activeThread?._id) return;
    try {
      const res = await getDMThread(activeThread._id);
      const thread = res.data.thread;
      setSettingsThread(thread);
      setSettingsTitle(thread.subject || '');
      mergeThreadIntoState(thread);
    } catch (err) {
      alert(err?.response?.data?.message || 'Unable to load conversation settings');
      setShowSettings(false);
    }
  }, [activeThread?._id, mergeThreadIntoState]);

  const handleToggleSettings = async () => {
    const next = !showSettings;
    setShowSettings(next);
    setSettingsQuery('');
    setSettingsMatches([]);
    if (next) {
      await loadThreadSettings();
    }
  };

  useEffect(() => {
    const query = settingsQuery.trim();
    if (!showSettings || !query) {
      setSettingsMatches([]);
      return;
    }

    let ignore = false;
    lookupUsers(query)
      .then((res) => {
        if (ignore) return;
        const users = res.data.users || [];
        const existingIds = new Set((settingsThread?.participants || []).map((p) => String(p._id || p)));
        setSettingsMatches(users.filter((entry) => !existingIds.has(String(entry._id))));
      })
      .catch(() => {
        if (!ignore) setSettingsMatches([]);
      });

    return () => {
      ignore = true;
    };
  }, [showSettings, settingsQuery, settingsThread?.participants]);

  const handleSaveTitle = async () => {
    if (!settingsThread?._id) return;
    setSettingsSaving(true);
    try {
      const res = await updateDMThread(settingsThread._id, { subject: settingsTitle });
      setSettingsThread(res.data.thread);
      mergeThreadIntoState(res.data.thread);
    } catch (err) {
      alert(err?.response?.data?.message || 'Unable to update conversation name');
    } finally {
      setSettingsSaving(false);
    }
  };

  const handleAddParticipant = async (userId) => {
    if (!settingsThread?._id) return;
    setSettingsSaving(true);
    try {
      const res = await addDMParticipant(settingsThread._id, userId);
      setSettingsThread(res.data.thread);
      mergeThreadIntoState(res.data.thread);
      setSettingsQuery('');
      setSettingsMatches([]);
    } catch (err) {
      alert(err?.response?.data?.message || 'Unable to add participant');
    } finally {
      setSettingsSaving(false);
    }
  };

  const handleRemoveParticipant = async (userId) => {
    if (!settingsThread?._id) return;
    setSettingsSaving(true);
    try {
      const res = await removeDMParticipant(settingsThread._id, userId);
      setSettingsThread(res.data.thread);
      mergeThreadIntoState(res.data.thread);
    } catch (err) {
      alert(err?.response?.data?.message || 'Unable to remove participant');
    } finally {
      setSettingsSaving(false);
    }
  };

  const handleToggleModerator = async (userId, isModerator) => {
    if (!settingsThread?._id) return;
    setSettingsSaving(true);
    try {
      const res = await setDMModerator(settingsThread._id, userId, !isModerator);
      setSettingsThread(res.data.thread);
      mergeThreadIntoState(res.data.thread);
    } catch (err) {
      alert(err?.response?.data?.message || 'Unable to update moderator role');
    } finally {
      setSettingsSaving(false);
    }
  };

  const startCompose = () => {
    setActiveThread(null);
    setMessages([]);
    setComposeOpen(true);
    setComposeToQuery('');
    setComposeMatches([]);
    setComposeRecipient(null);
    setComposeBody('');
    setComposeFiles([]);
    if (isMobileViewport) setMobilePane('detail');
  };

  const handleBackToList = () => {
    setComposeOpen(false);
    setActiveThread(null);
    setMessages([]);
    setShowSettings(false);
    setMobilePane('list');
  };

  const handleCreateConversation = async () => {
    if (!composeRecipient) {
      alert('Please select a recipient');
      return;
    }
    if (!composeBody.trim() && composeFiles.length === 0) {
      alert('Message body or attachments are required');
      return;
    }

    setComposeSending(true);
    try {
      const uploadedAttachments = [];
      for (const file of composeFiles) {
        const fileRes = await uploadFile(null, file, null, [composeRecipient._id]);
        uploadedAttachments.push({
          fileRef: fileRes.data.file._id,
          name: fileRes.data.file.originalName || file.name,
        });
      }

      const res = await createOrFindDM({
        recipientId: composeRecipient._id,
        body: composeBody.trim() || undefined,
        attachments: uploadedAttachments,
      });

      const thread = res.data.thread;
      setThreads((prev) => [
        thread,
        ...prev.filter((item) => item._id !== thread._id),
      ]);

      setComposeOpen(false);
      setComposeToQuery('');
      setComposeMatches([]);
      setComposeRecipient(null);
      setComposeBody('');
      setComposeFiles([]);

      await openThread(thread);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to start conversation');
    } finally {
      setComposeSending(false);
    }
  };

  // Get the other participant (not the current user) for display
  const getOther = (thread) =>
    (thread.participants || []).find((p) => String(p._id) !== String(user?._id)) || {};

  const isSettingsManager = Boolean(
    settingsThread && (
      String(settingsThread.createdBy?._id || settingsThread.createdBy) === String(user?._id) ||
      (settingsThread.moderators || []).some((m) => String(m._id || m) === String(user?._id))
    )
  );

  const settingsModeratorIds = new Set((settingsThread?.moderators || []).map((m) => String(m._id || m)));

  const handleComposeFileChange = (e) => {
    const nextFiles = Array.from(e.target.files || []);
    if (nextFiles.length === 0) return;
    setComposeFiles((prev) => [...prev, ...nextFiles]);
    e.target.value = '';
  };

  return (
    <div className={`messages-page ${isMobileViewport ? `mobile-pane-${mobilePane}` : ''}`}>
      {/* Thread list */}
      <div className="dm-thread-panel">
        <div className="dm-panel-header">
          <span>Direct Messages</span>
          <div className="dm-header-actions">
            <button className="dm-btn dm-btn-primary" onClick={startCompose} title="New Message">
              + New Msg
            </button>
            <button className="btn-icon" onClick={fetchDMs} title="Refresh">↻</button>
          </div>
        </div>

        {loading ? (
          <p className="dm-muted">Loading...</p>
        ) : threads.length === 0 ? (
          <p className="dm-muted">No messages yet.<br />Go to Contacts to start a conversation.</p>
        ) : (
          <ul className="dm-thread-list">
            {threads.map((t) => {
              const other = getOther(t);
              return (
                <li
                  key={t._id}
                  className={`dm-thread-item${activeThread?._id === t._id ? ' active' : ''}${t.unreadCount > 0 ? ' unread' : ''}`}
                  onClick={() => openThread(t)}
                >
                  <div className="dm-avatar">{(other.name || '?')[0].toUpperCase()}</div>
                  <div className="dm-thread-info">
                    <span className="dm-thread-name">{getThreadDisplayName(t)}</span>
                    <span className="dm-thread-time">{timeAgo(t.lastMessageAt)}</span>
                  </div>
                  {t.unreadCount > 0 && (
                    <span className="dm-unread-badge">{t.unreadCount}</span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Message panel */}
      <div className="dm-message-panel">
        {composeOpen ? (
          <div className="dm-compose-panel">
            {isMobileViewport && (
              <button type="button" className="dm-btn dm-btn-ghost dm-mobile-back" onClick={handleBackToList}>
                ← Conversations
              </button>
            )}
            <h2>New Message</h2>
            <div className="dm-compose-field">
              <label>To</label>
              <input
                value={composeRecipient ? composeRecipient.name : composeToQuery}
                onChange={(e) => {
                  setComposeRecipient(null);
                  setComposeToQuery(e.target.value);
                }}
                placeholder="Type a user name or email"
              />
              {!composeRecipient && composeToQuery.trim() && composeMatches.length > 0 && (
                <ul className="dm-compose-matches">
                  {composeMatches.map((entry) => (
                    <li
                      key={entry._id}
                      onClick={() => {
                        setComposeRecipient(entry);
                        setComposeToQuery(entry.name);
                        setComposeMatches([]);
                      }}
                    >
                      <span>{entry.name}</span>
                      <small>{entry.email}</small>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="dm-compose-field">
              <label>Message (optional)</label>
              <textarea
                value={composeBody}
                onChange={(e) => setComposeBody(e.target.value)}
                rows={8}
                placeholder="Write a first message..."
              />
            </div>

            <div className="dm-compose-field">
              <label>Attachments</label>
              <div className="dm-compose-attach-bar">
                <button
                  type="button"
                  className="dm-attach-trigger"
                  onClick={() => composeFileRef.current?.click()}
                >
                  +
                </button>
                <span className="dm-attach-label">Add files</span>
              </div>
              <input
                ref={composeFileRef}
                type="file"
                multiple
                className="dm-compose-file-input"
                onChange={handleComposeFileChange}
              />
              {composeFiles.length > 0 && (
                <ul className="dm-compose-files">
                  {composeFiles.map((file, idx) => (
                    <li key={`${file.name}-${idx}`}>
                      <span>{file.name}</span>
                      <button
                        type="button"
                        className="dm-btn dm-btn-ghost"
                        onClick={() => setComposeFiles((prev) => prev.filter((_, i) => i !== idx))}
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="dm-compose-actions">
              <button
                className="dm-btn dm-btn-ghost"
                onClick={() => setComposeOpen(false)}
                disabled={composeSending}
              >
                Cancel
              </button>
              <button
                className="dm-btn dm-btn-primary"
                onClick={handleCreateConversation}
                disabled={composeSending}
              >
                {composeSending ? 'Starting...' : 'Start Chat'}
              </button>
            </div>
          </div>
        ) : !activeThread ? (
          <div className="dm-empty-state">
            <span className="dm-empty-icon">💬</span>
            <p>Select a conversation or go to <strong>Contacts</strong> to start a new one</p>
          </div>
        ) : (
          <>
            <div className="dm-msg-header">
              {isMobileViewport && (
                <button type="button" className="dm-btn dm-btn-ghost dm-mobile-back" onClick={handleBackToList}>
                  ← Conversations
                </button>
              )}
              <div className="dm-header-avatar">
                {(getThreadDisplayName(activeThread) || '?')[0].toUpperCase()}
              </div>
              <div>
                <h2 className="dm-header-name">{getThreadDisplayName(activeThread)}</h2>
                <span className="dm-header-sub">Direct Message</span>
              </div>
              <button
                type="button"
                className="dm-header-settings-btn"
                onClick={handleToggleSettings}
                title="Conversation settings"
              >
                ⚙
              </button>
            </div>

            {showSettings && settingsThread && (
              <div className="dm-settings-panel">
                <div className="dm-settings-row">
                  <label>Conversation Name</label>
                  <div className="dm-settings-inline">
                    <input
                      value={settingsTitle}
                      onChange={(e) => setSettingsTitle(e.target.value)}
                      disabled={!isSettingsManager || settingsSaving}
                      placeholder="Direct Message"
                    />
                    <button
                      type="button"
                      className="dm-btn dm-btn-primary"
                      onClick={handleSaveTitle}
                      disabled={!isSettingsManager || settingsSaving}
                    >
                      Save
                    </button>
                  </div>
                </div>

                <div className="dm-settings-row">
                  <label>Add People</label>
                  <input
                    value={settingsQuery}
                    onChange={(e) => setSettingsQuery(e.target.value)}
                    disabled={!isSettingsManager || settingsSaving}
                    placeholder="Search people to add"
                  />
                  {settingsMatches.length > 0 && (
                    <ul className="dm-settings-matches">
                      {settingsMatches.map((entry) => (
                        <li key={entry._id}>
                          <span>{entry.name}</span>
                          <button
                            type="button"
                            className="dm-btn dm-btn-primary"
                            disabled={!isSettingsManager || settingsSaving}
                            onClick={() => handleAddParticipant(entry._id)}
                          >
                            Add
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="dm-settings-row">
                  <label>Participants</label>
                  <ul className="dm-settings-participants">
                    {(settingsThread.participants || []).map((participant) => {
                      const participantId = String(participant._id || participant);
                      const isSelf = participantId === String(user?._id);
                      const isCreator = participantId === String(settingsThread.createdBy?._id || settingsThread.createdBy);
                      const isModerator = isCreator || settingsModeratorIds.has(participantId);
                      return (
                        <li key={participantId}>
                          <div className="dm-settings-participant-meta">
                            <span>{participant.name || 'User'}</span>
                            {isCreator ? <em>Creator</em> : isModerator ? <em>Moderator</em> : null}
                          </div>
                          <div className="dm-settings-actions">
                            {isSettingsManager && !isCreator && (
                              <button
                                type="button"
                                className="dm-btn dm-btn-ghost"
                                onClick={() => handleToggleModerator(participantId, isModerator)}
                                disabled={settingsSaving}
                              >
                                {isModerator ? 'Remove Mod' : 'Make Mod'}
                              </button>
                            )}
                            {(isSettingsManager || isSelf) && !isCreator && (
                              <button
                                type="button"
                                className="dm-btn dm-btn-ghost"
                                onClick={() => handleRemoveParticipant(participantId)}
                                disabled={settingsSaving}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            )}

            <div className="dm-messages">
              {msgLoading ? (
                <p className="dm-muted">Loading messages...</p>
              ) : messages.length === 0 ? (
                <p className="dm-muted">No messages yet — say hello!</p>
              ) : (
                messages.map((msg) => {
                  const isMine = String(msg.sender?._id) === String(user?._id);
                  return (
                    <div
                      key={msg._id}
                      className={`dm-bubble-row ${isMine ? 'mine' : 'theirs'}`}
                    >
                      {!isMine && (
                        <div className="dm-bubble-avatar">
                          {(msg.sender?.name || '?')[0].toUpperCase()}
                        </div>
                      )}
                      <div className="dm-bubble">
                        <div className="dm-bubble-body">
                          {msg.mailboxId && (
                            <div className="dm-mailbox-label">📫 {msg.mailboxId.name}</div>
                          )}
                          {msg.isDeleted ? <em>Message deleted</em> : msg.body}
                          {Array.isArray(msg.attachments) && msg.attachments.length > 0 && (
                            <div className="dm-attachments">
                              {msg.attachments.map((attachment, idx) => {
                                const fileId = attachment.fileRef?._id || attachment.fileRef;
                                const fileName = attachment.name || attachment.fileRef?.originalName || 'Attachment';
                                return (
                                  <div className="dm-attachment-item" key={fileId || `${msg._id}-att-${idx}`}>
                                    <button
                                      type="button"
                                      className="dm-attachment-link"
                                      onClick={() => openFile(fileId).catch(() => alert('Unable to open attachment'))}
                                      title="Open in new tab"
                                    >
                                      📎 {fileName}
                                    </button>
                                    <button
                                      type="button"
                                      className="dm-attachment-download"
                                      onClick={() => downloadFile(fileId, fileName)}
                                      title="Download"
                                    >
                                      ⬇
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                        <div className="dm-bubble-time">
                          {timeAgo(msg.createdAt)}
                          {isMine && !msg.isDeleted && (
                            <button
                              type="button"
                              className="dm-bubble-delete-btn"
                              onClick={() => handleDeleteMessage(msg._id)}
                              title="Delete message"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={bottomRef} />
            </div>

            <MessageCompose
              onSend={handleSend}
              placeholder={`Message ${getThreadDisplayName(activeThread) || ''}…`}
            />
          </>
        )}
      </div>
    </div>
  );
}
